import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tenantApi, adminApi, customDomainApi, tenantMediaApi, type TenantAdmin, type TenantI18nField, type Tenant, type CustomDomain } from "@/lib/api.js";
import { I18nEditor, EMPTY_I18N } from "@/components/I18nEditor.js";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import { MediaPickerField } from "@/components/MediaPickerField.js";
import type { Language } from "@templestay/i18n";

type I18nValue = Record<Language, Record<string, string>>;

const ALL_FIELDS = [
  { key: "name", label: "사찰명" },
  { key: "card_title", label: "카드 제목" },
  { key: "card_body", label: "카드 본문", multiline: true },
  { key: "card_footnote", label: "카드 각주" },
  { key: "card_button_label", label: "버튼 레이블" },
];

const BRANDING_TITLE_FIELDS = [{ key: "html_title", label: "브라우저 탭 제목" }];
const BRANDING_FOOTER_FIELDS = [
  { key: "footer_name", label: "사찰명" },
  { key: "footer_address", label: "주소" },
  { key: "footer_phone", label: "전화번호" },
  { key: "footer_copyright", label: "저작권" },
];
const BRANDING_SPLASH_FIELDS = [
  { key: "splash_headline", label: "헤드라인" },
  { key: "splash_subheadline", label: "서브헤드라인" },
];

interface TenantTheme {
  html_title?: Record<string, string>;
  favicon_url?: string;
  logo_url?: string;
  footer?: {
    name?: Record<string, string>;
    address?: Record<string, string>;
    phone?: Record<string, string>;
    copyright?: Record<string, string>;
  };
  splash?: {
    headline?: Record<string, string>;
    subheadline?: Record<string, string>;
    name?: Record<string, string>;
    bg_color?: string;
    text_color?: string;
    dot_color?: string;
  };
}

function themeToI18n(theme: TenantTheme): I18nValue {
  const langs: Language[] = ["ko", "en", "zh", "ja"];
  const result: I18nValue = { ko: {}, en: {}, zh: {}, ja: {} };
  for (const lang of langs) {
    result[lang] = {
      html_title: theme.html_title?.[lang] ?? "",
      footer_name: theme.footer?.name?.[lang] ?? "",
      footer_address: theme.footer?.address?.[lang] ?? "",
      footer_phone: theme.footer?.phone?.[lang] ?? "",
      footer_copyright: theme.footer?.copyright?.[lang] ?? "",
      splash_headline: theme.splash?.headline?.[lang] ?? theme.splash?.name?.[lang] ?? "",
      splash_subheadline: theme.splash?.subheadline?.[lang] ?? "",
    };
  }
  return result;
}

function i18nToTheme(
  i18n: I18nValue,
  faviconUrl: string,
  logoUrl: string,
  splashBgColor: string,
  splashTextColor: string,
  splashDotColor: string,
  existingTheme: TenantTheme
): TenantTheme {
  const langs: Language[] = ["ko", "en", "zh", "ja"];
  const htmlTitle: Record<string, string> = {};
  const footerName: Record<string, string> = {};
  const footerAddress: Record<string, string> = {};
  const footerPhone: Record<string, string> = {};
  const footerCopyright: Record<string, string> = {};
  const splashHeadline: Record<string, string> = {};
  const splashSubheadline: Record<string, string> = {};
  for (const lang of langs) {
    if (i18n[lang].html_title) htmlTitle[lang] = i18n[lang].html_title;
    if (i18n[lang].footer_name) footerName[lang] = i18n[lang].footer_name;
    if (i18n[lang].footer_address) footerAddress[lang] = i18n[lang].footer_address;
    if (i18n[lang].footer_phone) footerPhone[lang] = i18n[lang].footer_phone;
    if (i18n[lang].footer_copyright) footerCopyright[lang] = i18n[lang].footer_copyright;
    if (i18n[lang].splash_headline) splashHeadline[lang] = i18n[lang].splash_headline;
    if (i18n[lang].splash_subheadline) splashSubheadline[lang] = i18n[lang].splash_subheadline;
  }
  return {
    ...existingTheme,
    ...(Object.keys(htmlTitle).length && { html_title: htmlTitle }),
    ...(faviconUrl.trim() && { favicon_url: faviconUrl.trim() }),
    ...(logoUrl.trim() && { logo_url: logoUrl.trim() }),
    footer: {
      ...(Object.keys(footerName).length && { name: footerName }),
      ...(Object.keys(footerAddress).length && { address: footerAddress }),
      ...(Object.keys(footerPhone).length && { phone: footerPhone }),
      ...(Object.keys(footerCopyright).length && { copyright: footerCopyright }),
    },
    splash: {
      ...(Object.keys(splashHeadline).length && { headline: splashHeadline }),
      ...(Object.keys(splashSubheadline).length && { subheadline: splashSubheadline }),
      ...(splashBgColor.trim() && { bg_color: splashBgColor.trim() }),
      ...(splashTextColor.trim() && { text_color: splashTextColor.trim() }),
      ...(splashDotColor.trim() && { dot_color: splashDotColor.trim() }),
    },
  };
}

function safeJson(s: string): Record<string, string> {
  try { return JSON.parse(s) as Record<string, string>; } catch { return {}; }
}

function tenantToI18n(tenant: Tenant): I18nValue {
  const langs: Language[] = ["ko", "en", "zh", "ja"];
  const name = safeJson(tenant.name);
  const title = safeJson(tenant.card_title);
  const body = safeJson(tenant.card_body);
  const footnote = safeJson(tenant.card_footnote);
  const btnLabel = safeJson(tenant.card_button_label);
  const result: I18nValue = { ko: {}, en: {}, zh: {}, ja: {} };
  for (const lang of langs) {
    result[lang] = {
      name: name[lang] ?? "",
      card_title: title[lang] ?? "",
      card_body: body[lang] ?? "",
      card_footnote: footnote[lang] ?? "",
      card_button_label: btnLabel[lang] ?? "",
    };
  }
  return result;
}

function i18nToFields(i18n: I18nValue) {
  const extract = (field: string): TenantI18nField => ({
    ko: i18n.ko[field] ?? "",
    en: i18n.en[field] ?? "",
    zh: i18n.zh[field] ?? "",
    ja: i18n.ja[field] ?? "",
  });
  return {
    name: extract("name"),
    card_title: extract("card_title"),
    card_body: extract("card_body"),
    card_footnote: extract("card_footnote"),
    card_button_label: extract("card_button_label"),
  };
}

export function TenantEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [slug, setSlug] = useState("");
  const [i18n, setI18n] = useState<I18nValue>(EMPTY_I18N);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [cardImageUrl, setCardImageUrl] = useState<string | null>(null);
  const [cardButtonUrl, setCardButtonUrl] = useState("");
  const [brandingI18n, setBrandingI18n] = useState<I18nValue>(EMPTY_I18N);
  const [faviconUrl, setFaviconUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [splashBgColor, setSplashBgColor] = useState("");
  const [splashTextColor, setSplashTextColor] = useState("");
  const [splashDotColor, setSplashDotColor] = useState("");
  const [removeTarget, setRemoveTarget] = useState<TenantAdmin | null>(null);
  const [addingAdminId, setAddingAdminId] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [deleteDomainTarget, setDeleteDomainTarget] = useState<CustomDomain | null>(null);

  const { data: tenant, isLoading: tenantLoading } = useQuery({
    queryKey: ["tenant", id],
    queryFn: () => tenantApi.get(id!),
    enabled: !isNew,
  });

  const { data: assignments, isLoading: assignmentsLoading } = useQuery({
    queryKey: ["tenant-assignments", id],
    queryFn: () => tenantApi.getAssignments(id!),
    enabled: !isNew,
  });

  const { data: allAdmins } = useQuery({
    queryKey: ["all-admins"],
    queryFn: () => adminApi.list(),
    enabled: !isNew,
  });

  const { data: customDomains, isLoading: domainsLoading } = useQuery({
    queryKey: ["tenant-custom-domains", id],
    queryFn: () => customDomainApi.list(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (tenant) {
      setSlug(tenant.slug);
      setI18n(tenantToI18n(tenant));
      setDisplayOrder(tenant.display_order);
      setCardImageUrl(tenant.card_image_url ?? null);
      setCardButtonUrl(tenant.card_button_url ?? "");
      try {
        const theme: TenantTheme = tenant.theme ? JSON.parse(tenant.theme) : {};
        setBrandingI18n(themeToI18n(theme));
        setFaviconUrl(theme.favicon_url ?? "");
        setLogoUrl(theme.logo_url ?? "");
        setSplashBgColor(theme.splash?.bg_color ?? "");
        setSplashTextColor(theme.splash?.text_color ?? "");
        setSplashDotColor(theme.splash?.dot_color ?? "");
      } catch { /* ignore */ }
    }
  }, [tenant]);

  const createMutation = useMutation({
    mutationFn: () => {
      const fields = i18nToFields(i18n);
      return tenantApi.create({
        slug: slug.trim(),
        ...fields,
        card_button_url: cardButtonUrl.trim() || undefined,
      });
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      navigate(`/tenants/${created.id}/edit`, { replace: true });
    },
    onError: (err) => {
      alert(err instanceof Error ? err.message : "생성에 실패했습니다.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      const fields = i18nToFields(i18n);
      const existingTheme: TenantTheme = (() => {
        try { return tenant?.theme ? JSON.parse(tenant.theme) : {}; } catch { return {}; }
      })();
      const theme = i18nToTheme(brandingI18n, faviconUrl, logoUrl, splashBgColor, splashTextColor, splashDotColor, existingTheme);
      return tenantApi.update(id!, {
        ...fields,
        display_order: displayOrder,
        card_image_url: cardImageUrl,
        card_button_url: cardButtonUrl.trim() || undefined,
        theme: JSON.stringify(theme),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      qc.invalidateQueries({ queryKey: ["tenant", id] });
      alert("저장되었습니다.");
    },
    onError: (err) => {
      alert(err instanceof Error ? err.message : "저장에 실패했습니다.");
    },
  });

  const addAssignmentMutation = useMutation({
    mutationFn: (userId: string) => tenantApi.addAssignment(id!, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-assignments", id] });
      setAddingAdminId("");
    },
    onError: (err) => {
      alert(err instanceof Error ? err.message : "관리자 추가에 실패했습니다.");
    },
  });

  const removeAssignmentMutation = useMutation({
    mutationFn: (userId: string) => tenantApi.removeAssignment(id!, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-assignments", id] });
      setRemoveTarget(null);
    },
    onError: (err) => {
      alert(err instanceof Error ? err.message : "관리자 제거에 실패했습니다.");
      setRemoveTarget(null);
    },
  });

  const addDomainMutation = useMutation({
    mutationFn: (domain: string) => customDomainApi.add(id!, domain),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-custom-domains", id] });
      setNewDomain("");
    },
    onError: (err) => {
      alert(err instanceof Error ? err.message : "도메인 추가에 실패했습니다.");
    },
  });

  const verifyDomainMutation = useMutation({
    mutationFn: (domainId: string) => customDomainApi.verify(id!, domainId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-custom-domains", id] });
    },
    onError: (err) => {
      alert(err instanceof Error ? err.message : "도메인 검증에 실패했습니다.");
    },
  });

  const deleteDomainMutation = useMutation({
    mutationFn: (domainId: string) => customDomainApi.delete(id!, domainId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-custom-domains", id] });
      setDeleteDomainTarget(null);
    },
    onError: (err) => {
      alert(err instanceof Error ? err.message : "도메인 삭제에 실패했습니다.");
      setDeleteDomainTarget(null);
    },
  });

  function handleSave() {
    if (!slug.trim()) { alert("슬러그를 입력해주세요."); return; }
    if (isNew) createMutation.mutate();
    else updateMutation.mutate();
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const assignedIds = new Set((assignments ?? []).map((a) => a.user_id));
  const availableAdmins = (allAdmins ?? []).filter((a) => !assignedIds.has(a.id));

  if (!isNew && tenantLoading) {
    return (
      <div className="flex-1 overflow-y-auto p-8">
        <div className="flex items-center justify-center py-24">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="mb-6 flex items-center gap-4">
        <Link
          to="/tenants"
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-50"
        >
          ← 목록
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          {isNew ? "사찰 추가" : "사찰 수정"}
        </h1>
      </div>

      <div className="mx-auto max-w-2xl space-y-6">
        {/* 슬러그 */}
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-4 text-sm font-bold text-gray-700">기본 정보</h2>
          <label className="mb-1 block text-xs font-medium text-gray-500">
            슬러그 <span className="text-red-400">*</span>
            {!isNew && <span className="ml-2 text-gray-300">(수정 불가)</span>}
          </label>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
            disabled={!isNew}
            placeholder="bongeunsa"
            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-mono outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 disabled:bg-gray-50 disabled:text-gray-400"
          />
          {isNew && (
            <p className="mt-1 text-xs text-gray-400">
              영문 소문자, 숫자, 하이픈만 사용. 생성 후 변경 불가.
            </p>
          )}
          <div className="mt-4">
            <label className="mb-1 block text-xs font-medium text-gray-500">
              노출 순서 <span className="text-gray-400">(낮은 숫자가 먼저 표시. 목록에서 드래그로도 조정 가능)</span>
            </label>
            <input
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(Number(e.target.value))}
              min={0}
              className="w-32 rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
            />
          </div>
        </div>

        {/* 다국어 콘텐츠 */}
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-4 text-sm font-bold text-gray-700">사찰명 / 카드 콘텐츠</h2>
          <I18nEditor
            value={i18n}
            fields={ALL_FIELDS}
            onChange={setI18n}
            defaultLang="ko"
            requiredFields={["name"]}
          />
          {!isNew && (
            <div className="mt-4 space-y-4">
              <MediaPickerField
                label="카드 이미지"
                value={cardImageUrl ?? ""}
                onChange={(url) => setCardImageUrl(url || null)}
                queryKey={["tenant-media", id ?? ""]}
                listFn={() => tenantMediaApi.list(id!)}
                uploadFn={(file) => tenantMediaApi.upload(id!, file)}
                deleteFn={(key) => tenantMediaApi.delete(id!, key)}
              />
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">카드 버튼 URL</label>
                <input
                  type="url"
                  value={cardButtonUrl}
                  onChange={(e) => setCardButtonUrl(e.target.value)}
                  placeholder="https://bongeunsa.templestay.app"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
                />
              </div>
            </div>
          )}
          {isNew && (
            <div className="mt-4">
              <label className="mb-1 block text-xs font-medium text-gray-500">카드 버튼 URL</label>
              <input
                type="url"
                value={cardButtonUrl}
                onChange={(e) => setCardButtonUrl(e.target.value)}
                placeholder="https://bongeunsa.templestay.app"
                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
              />
            </div>
          )}
        </div>

        {/* 브랜딩 설정 */}
        {!isNew && (
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
            <h2 className="mb-4 text-sm font-bold text-gray-700">브랜딩 설정</h2>

            <div className="space-y-5">
              <div className="space-y-4">
                <MediaPickerField
                  label="Favicon"
                  value={faviconUrl}
                  onChange={setFaviconUrl}
                  queryKey={["tenant-media", id ?? ""]}
                  listFn={() => tenantMediaApi.list(id!)}
                  uploadFn={(file) => tenantMediaApi.upload(id!, file)}
                  deleteFn={(key) => tenantMediaApi.delete(id!, key)}
                />
                <MediaPickerField
                  label="로고"
                  value={logoUrl}
                  onChange={setLogoUrl}
                  queryKey={["tenant-media", id ?? ""]}
                  listFn={() => tenantMediaApi.list(id!)}
                  uploadFn={(file) => tenantMediaApi.upload(id!, file)}
                  deleteFn={(key) => tenantMediaApi.delete(id!, key)}
                />
              </div>

              <div>
                <p className="mb-2 text-xs font-medium text-gray-500">브라우저 탭 제목</p>
                <I18nEditor
                  value={brandingI18n}
                  fields={BRANDING_TITLE_FIELDS}
                  onChange={setBrandingI18n}
                  defaultLang="ko"
                />
              </div>

              <div>
                <p className="mb-2 text-xs font-medium text-gray-500">푸터</p>
                <I18nEditor
                  value={brandingI18n}
                  fields={BRANDING_FOOTER_FIELDS}
                  onChange={setBrandingI18n}
                  defaultLang="ko"
                />
              </div>

              <div>
                <p className="mb-2 text-xs font-medium text-gray-500">스플래시 화면</p>
                <I18nEditor
                  value={brandingI18n}
                  fields={BRANDING_SPLASH_FIELDS}
                  onChange={setBrandingI18n}
                  defaultLang="ko"
                />
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-gray-500">배경 색상</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={splashBgColor || "#f5f4ee"}
                        onChange={(e) => setSplashBgColor(e.target.value)}
                        className="h-9 w-10 cursor-pointer rounded border border-gray-200 p-0.5"
                      />
                      <input
                        type="text"
                        value={splashBgColor}
                        onChange={(e) => setSplashBgColor(e.target.value)}
                        placeholder="#4C4A31"
                        className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-xs font-mono outline-none focus:border-brand-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-500">텍스트 색상</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={splashTextColor || "#1c1917"}
                        onChange={(e) => setSplashTextColor(e.target.value)}
                        className="h-9 w-10 cursor-pointer rounded border border-gray-200 p-0.5"
                      />
                      <input
                        type="text"
                        value={splashTextColor}
                        onChange={(e) => setSplashTextColor(e.target.value)}
                        placeholder="#1c1917"
                        className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-xs font-mono outline-none focus:border-brand-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-500">점 색상</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={splashDotColor || "#4c4a31"}
                        onChange={(e) => setSplashDotColor(e.target.value)}
                        className="h-9 w-10 cursor-pointer rounded border border-gray-200 p-0.5"
                      />
                      <input
                        type="text"
                        value={splashDotColor}
                        onChange={(e) => setSplashDotColor(e.target.value)}
                        placeholder="#4c4a31"
                        className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-xs font-mono outline-none focus:border-brand-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 관리자 배정 */}
        {!isNew && (
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
            <h2 className="mb-4 text-sm font-bold text-gray-700">관리자 배정</h2>

            {availableAdmins.length > 0 && (
              <div className="mb-4 flex gap-2">
                <select
                  value={addingAdminId}
                  onChange={(e) => setAddingAdminId(e.target.value)}
                  className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400"
                >
                  <option value="">관리자 선택...</option>
                  {availableAdmins.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.email}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => addingAdminId && addAssignmentMutation.mutate(addingAdminId)}
                  disabled={!addingAdminId || addAssignmentMutation.isPending}
                  className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  추가
                </button>
              </div>
            )}

            {assignmentsLoading ? (
              <p className="py-4 text-center text-sm text-gray-400">불러오는 중...</p>
            ) : !assignments?.length ? (
              <p className="py-4 text-center text-sm text-gray-400">배정된 관리자가 없습니다.</p>
            ) : (
              <div className="divide-y divide-gray-50 rounded-xl border border-gray-100">
                {assignments.map((a) => (
                  <div key={a.user_id} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{a.admin_email}</p>
                      <p className="text-xs text-gray-400">
                        배정일: {new Date(a.created_at).toLocaleDateString("ko-KR")}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setRemoveTarget(a)}
                      className="rounded-lg px-2 py-1.5 text-xs text-red-400 hover:bg-red-50 hover:text-red-600"
                    >
                      제거
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 커스텀 도메인 */}
        {!isNew && (
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
            <h2 className="mb-4 text-sm font-bold text-gray-700">커스텀 도메인</h2>

            <div className="mb-4 flex gap-2">
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value.toLowerCase())}
                placeholder="bongeunsa.app"
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
              />
              <button
                type="button"
                onClick={() => newDomain.trim() && addDomainMutation.mutate(newDomain.trim())}
                disabled={!newDomain.trim() || addDomainMutation.isPending}
                className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
              >
                추가
              </button>
            </div>

            {domainsLoading ? (
              <p className="py-4 text-center text-sm text-gray-400">불러오는 중...</p>
            ) : !customDomains?.length ? (
              <p className="py-4 text-center text-sm text-gray-400">등록된 커스텀 도메인이 없습니다.</p>
            ) : (
              <div className="space-y-3">
                {customDomains.map((d) => (
                  <div key={d.id} className="rounded-xl border border-gray-100 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium text-gray-800">{d.domain}</span>
                        {d.status === "pending" && (
                          <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                            검증 대기
                          </span>
                        )}
                        {d.status === "verified" && (
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                            검증 완료
                          </span>
                        )}
                        {d.status === "failed" && (
                          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                            검증 실패
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {d.status !== "verified" && (
                          <button
                            type="button"
                            onClick={() => verifyDomainMutation.mutate(d.id)}
                            disabled={verifyDomainMutation.isPending}
                            className="rounded-lg px-2 py-1.5 text-xs text-brand-600 hover:bg-brand-50 disabled:opacity-50"
                          >
                            검증완료
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setDeleteDomainTarget(d)}
                          className="rounded-lg px-2 py-1.5 text-xs text-red-400 hover:bg-red-50 hover:text-red-600"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                    {d.status === "pending" && (
                      <div className="mt-3 rounded-lg bg-gray-50 p-3 text-xs text-gray-500">
                        <p className="mb-1 font-medium text-gray-600">다음 CNAME 레코드를 DNS에 추가하세요:</p>
                        <code className="block font-mono">
                          {d.domain}&nbsp;&nbsp;CNAME&nbsp;&nbsp;templestay.app
                        </code>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 저장/취소 */}
        <div className="flex justify-end gap-3 pb-8">
          <Link
            to="/tenants"
            className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            취소
          </Link>
          <button
            type="button"
            onClick={handleSave}
            disabled={isPending}
            className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {isPending ? "저장 중..." : isNew ? "사찰 생성" : "저장"}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={!!removeTarget}
        title="관리자 배정 제거"
        description={`${removeTarget?.admin_email} 관리자를 이 사찰에서 제거할까요?`}
        confirmLabel="제거"
        danger
        onConfirm={() => removeTarget && removeAssignmentMutation.mutate(removeTarget.user_id)}
        onCancel={() => setRemoveTarget(null)}
      />

      <ConfirmDialog
        open={!!deleteDomainTarget}
        title="커스텀 도메인 삭제"
        description={`${deleteDomainTarget?.domain} 도메인을 삭제할까요? 삭제 후 해당 도메인으로 접속이 불가합니다.`}
        confirmLabel="삭제"
        danger
        onConfirm={() => deleteDomainTarget && deleteDomainMutation.mutate(deleteDomainTarget.id)}
        onCancel={() => setDeleteDomainTarget(null)}
      />
    </div>
  );
}
